export enum PlacementStatus {
  DRAFT = 'DRAFT',
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export enum PlacementUpdateStatus {
  DRAFT = 'DRAFT',
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
}

export enum PlacementBillingType {
  ADD_TO_CART = 'ADD_TO_CART',
  CLICK = 'CLICK',
  CPM = 'CPM',
}

export enum PlacementUpdateBillingType {
  ADD_TO_CART = 'ADD_TO_CART',
  CLICK = 'CLICK',
  CPM = 'CPM',
}

export interface AllowedBilling {
  billing_type: AllowedBillingType;
  min_bid_in_cents?: number | null;
  category_billings?: CategoryBilling[] | null;
}

export enum AllowedBillingType {
  CPM = 'CPM',
  CLICK = 'CLICK',
  ADD_TO_CART = 'ADD_TO_CART',
}

export interface CategoryBilling {
  category_key: string;
  min_bid_in_cents: number;
  inherited?: boolean | null;
}

export interface PlacementsAttribution {
  post_click: PlacementsAttributionSettings | null;
  post_view: PlacementsAttributionSettings | null;
}
export interface PlacementsAttributionSettings {
  lookback_window_days: number;
  product_match: PlacementsAttributionProductMatch;
}

export enum PlacementsAttributionProductMatch {
  SKU = 'SKU',
  CATEGORY = 'CATEGORY',
  BRAND = 'BRAND',
}

export interface PlacementsAttributionPostView {
  mode: PlacementsAttributionPostViewMode;
  lookback_window_days: number;
  product_match: PlacementsAttributionProductMatch;
}

export enum PlacementsAttributionPostViewMode {
  ALL_EVENTS = 'ALL_EVENTS',
}

export interface PlacementsAdvancedSettings {
  allow_frequency_capping: boolean | null;
  allow_pacing: boolean | null;
  auto_bidding: boolean | null;
  auto_keyword_skus_match: boolean | null;
  disable_ad_cache: boolean | null;
  serve_skus: boolean | null;
  show_inventory: boolean | null;
  forecasting_mode: ForecastingMode;
  bill_per_sku: boolean | null;
}

export enum ForecastingMode {
  DISABLED = 'disabled',
  RETAILER_ONLY = 'retailer_only',
  RETAILER_AND_ADVERTISERS = 'retailer_and_advertisers',
}

export enum PlacementRequiresApproval {
  ADVERTISED_SKUS = 'advertised_skus',
  KEYWORDS = 'keywords',
  AUDIENCE_ID = 'audience_id',
  HEADLINE = 'headline',
  CREATIVES = 'creatives',
  LINK_URL = 'link_url',
  DISCOUNT_VALUE = 'discount_value',
  DISCOUNT_TYPE = 'discount_type',
  CUSTOM_TARGETING = 'custom_targeting',
  TARGET_CATEGORY_KEYS = 'target_category_keys',
  CUSTOM_PARAMETERS = 'custom_parameters',
  GEO_TARGETS = 'geo_targets',
  SCHEDULE = 'schedule',
}

export enum PlacementCategoryTargetingMode {
  AUTOMATIC_SAME = 'AUTOMATIC_SAME',
  SELECTIVE_SAME = 'SELECTIVE_SAME',
  SELECTIVE_ALL = 'SELECTIVE_ALL',
}

export interface PlacementsCustomTargetingValue {
  type: PlacementsCustomTargetingValueType;
  enum?: string[];
  title?: string | null;
  description?: string | null;
}

export enum PlacementsCustomTargetingValueType {
  BOOLEAN = 'boolean',
  INTEGER = 'integer',
  STRING = 'string',
  INTEGER_LIST = 'integer_list',
  STRING_LIST = 'string_list',
}

export interface CreativesConfiguration {
  configuration: { [key: string]: CreativeProperties };
}

export interface DefaultCreativesConfiguration {
  configuration: {
    default: CreativeProperties;
  };
}

export interface CreativeProperties {
  title?: string;
  content_types?: string[];
  width: number | null;
  height: number | null;
  min_duration?: number | null;
  max_duration?: number | null;
}

export function isCreativesConfigurationSet(placement: RawPlacementApiResponse) {
  return (
    placement.creatives?.configuration && Object.keys(placement.creatives.configuration).length > 0
  );
}

export function areDimensionsSet(creative: CreativeProperties) {
  return Boolean(creative.width && creative.height);
}

export interface PlacementsPreviewTemplatesValue {
  title?: string;
  html_template: string;
}

export interface PlacementsGuidelines {
  keywords?: string | null;
  creatives?: string | null;
}

export interface RawPlacementApiResponse {
  ads_count: number;
  advanced_settings: PlacementsAdvancedSettings;
  allowed_advertiser_ids?: string[] | null;
  allowed_billings?: AllowedBilling[] | null;
  attribution?: PlacementsAttribution | null;
  catalog_key?: string | null;
  category_targeting_mode?: PlacementCategoryTargetingMode;
  created_at: string;
  creatives?: CreativesConfiguration | null;
  custom_targeting?: { [key: string]: PlacementsCustomTargetingValue } | null;
  custom_template?: any;
  description?: string | null;
  group?: string | null;
  guidelines?: PlacementsGuidelines;
  has_advertised_skus?: boolean | null;
  has_audience: boolean | null;
  has_creatives: boolean | null;
  has_discounts: boolean | null;
  has_dayparting: boolean | null;
  has_geo: boolean | null;
  has_keywords: boolean | null;
  has_link_url: boolean | null;
  has_skus: boolean | null;
  has_stores: boolean | null;
  id: string;
  image_url?: string | null;
  max_headline_length?: number | null;
  name: string;
  preview_templates?: { [key: string]: PlacementsPreviewTemplatesValue } | null;
  requires_approval?: PlacementRequiresApproval[];
  status: PlacementStatus;
  updated_at: string;
  weight_bid: number | null;
  weight_relevance: number | null;
}

export interface RawPlacementApiCreateRequest {
  ads_count: number;
  advanced_settings?: PlacementsAdvancedSettings | null;
  allowed_advertiser_ids?: string[] | null;
  allowed_billings?: AllowedBilling[] | null;
  attribution?: PlacementsAttribution | null;
  catalog_key?: string | null;
  category_targeting_mode?: PlacementCategoryTargetingMode;
  creatives?: CreativesConfiguration | null;
  custom_targeting?: { [key: string]: PlacementsCustomTargetingValue } | null;
  custom_template?: any;
  description?: string | null;
  group?: string | null;
  guidelines?: PlacementsGuidelines | null;
  has_advertised_skus?: boolean | null;
  has_audience: boolean | null;
  has_categories?: boolean | null;
  has_creatives?: boolean | null;
  has_dayparting: boolean | null;
  has_discounts: boolean | null;
  has_geo: boolean | null;
  has_keywords: boolean | null;
  has_link_url: boolean | null;
  has_skus: boolean | null;
  has_stores: boolean | null;
  image_url?: string | null;
  max_headline_length?: number | null;
  name: string;
  preview_templates?: { [key: string]: PlacementsPreviewTemplatesValue } | null;
  requires_approval?: PlacementRequiresApproval[];
  weight_bid?: number | null;
  weight_relevance?: number | null;
}

export interface RawPlacementApiUpdateRequest {
  ads_count: number;
  advanced_settings: PlacementsAdvancedSettings;
  allowed_advertiser_ids: string[] | null;
  allowed_billings: AllowedBilling[] | null;
  attribution: PlacementsAttribution | null;
  catalog_key: string | null;
  custom_targeting: any;
  custom_template: any;
  description: string | null;
  group: string | null;
  guidelines: PlacementsGuidelines;
  has_advertised_skus?: boolean | null;
  has_dayparting?: boolean | null;
  has_geo: boolean | null;
  has_keywords: boolean | null;
  has_link_url: boolean | null;
  has_skus: boolean | null;
  has_stores: boolean | null;
  image_url: string | null;
  max_headline_length: number | null;
  name: string;
  preview_templates: { [key: string]: PlacementsPreviewTemplatesValue } | null;
  requires_approval: PlacementRequiresApproval[];
  status: PlacementUpdateStatus;
  weight_bid: number | null;
  weight_relevance: number | null;
}

export interface PlacementMinBid {
  bid_cents?: number | null;
  category_key?: string | null;
}

export function rawPlacementApiResponseToRawPlacementApiUpdateRequest(
  data: RawPlacementApiResponse,
): RawPlacementApiUpdateRequest {
  let status: PlacementUpdateStatus | undefined = undefined;
  switch (data.status) {
    case PlacementStatus.DRAFT:
      status = PlacementUpdateStatus.DRAFT;
      break;
    case PlacementStatus.INACTIVE:
      status = PlacementUpdateStatus.INACTIVE;
      break;
    case PlacementStatus.ACTIVE:
      status = PlacementUpdateStatus.ACTIVE;
      break;
    default:
      status = undefined;
  }

  return {
    ads_count: data.ads_count,
    attribution: data.attribution ?? null,
    advanced_settings: data.advanced_settings,
    allowed_advertiser_ids: data.allowed_advertiser_ids ?? null,
    allowed_billings:
      Array.isArray(data.allowed_billings) && data.allowed_billings.length > 0
        ? data.allowed_billings
        : null,
    catalog_key: data.catalog_key ?? null,
    custom_targeting: data.custom_targeting,
    custom_template: data.custom_template,
    description: data.description ?? null,
    group: data.group ?? null,
    // The update contract requires a guidelines object; both of its fields are optional.
    guidelines: data.guidelines ?? {},
    has_advertised_skus: data.has_advertised_skus ?? null,
    has_dayparting: data.has_dayparting ?? null,
    has_keywords: data.has_keywords ?? null,
    has_link_url: data.has_link_url ?? null,
    has_skus: data.has_skus ?? null,
    has_geo: data.has_geo ?? null,
    has_stores: data.has_stores ?? null,
    image_url: data.image_url ?? null,
    max_headline_length: data.max_headline_length ?? null,
    name: data.name,
    preview_templates: data.preview_templates ?? null,
    requires_approval: data.requires_approval ?? [],
    status: status ?? PlacementUpdateStatus.INACTIVE,
    weight_bid: data.weight_bid ?? null,
    weight_relevance: data.weight_relevance ?? null,
  };
}

export function requiresApprovalIsOn(
  requires_approval: PlacementRequiresApproval[] | undefined,
  key: PlacementRequiresApproval,
) {
  return (requires_approval || []).includes(key);
}

export function isAllowedBillingTypeIsOn(
  allowed_billings: AllowedBilling[] | null | undefined,
  key: AllowedBillingType,
) {
  return (allowed_billings || []).some((billing) => billing.billing_type === key);
}

export function getMinBidForBillingType(
  allowed_billings: AllowedBilling[] | null | undefined,
  key: AllowedBillingType,
) {
  const billing = (allowed_billings || []).find((billing) => billing.billing_type === key);

  return billing?.min_bid_in_cents;
}

export function hasBillingType(
  placement: RawPlacementApiResponse,
  billingType: AllowedBillingType,
) {
  return (
    placement.allowed_billings?.some(
      (allowedBilling) => allowedBilling.billing_type === billingType,
    ) ?? false
  );
}

export interface RawPlacementKeywordSuggestionRequest {
  skus: string[];
}
